import { Type } from '@angular/core';
import { LoginOIDCStepComponent as OIDCIntroductionStepComponent } from './steps/oidc-introduction.step';
import { LanguageStepComponent } from './steps/language.step';
import { PushStepComponent } from './steps/push-notifications.step';

export interface WizardStep {
  id: string;
  /**
   * Revision in which this step was introduced.
   *
   * Add a step, give it the next number — that is the entire maintenance burden, and
   * `CURRENT_REVISION` follows on its own. Never renumber an existing step: installations
   * that already passed it would be sent through it again.
   */
  sinceRevision: number;
  /**
   * Whether someone installing the app for the first time sees this step.
   *
   * Setup steps say yes — they collect a decision everyone has to make. Announcements say
   * no: they are about a change, and for a fresh install there was nothing to change.
   */
  onFreshInstall: boolean;
  /** Becomes a translation key once this moves into the app. */
  title: string;
  component: Type<unknown>;
}

/**
 * The order here is the order the user walks through, independent of how the steps came
 * to be due.
 */
export const WIZARD_STEPS: readonly WizardStep[] = [
  {
    id: 'language',
    sinceRevision: 1,
    onFreshInstall: true,
    title: 'settings.lang.title',
    component: LanguageStepComponent,
  },
  {
    id: 'push',
    sinceRevision: 1,
    onFreshInstall: true,
    title: 'push-notification.title',
    component: PushStepComponent,
  },
  {
    id: 'oidc-introduction',
    sinceRevision: 2,
    onFreshInstall: false,
    title: 'wizard.login-oidc.title',
    component: OIDCIntroductionStepComponent,
  },
];

/**
 * Derived rather than declared, so there is no constant anyone can forget to raise. A
 * `reduce` and not `Math.max(...)`, which answers `-Infinity` for an empty catalogue.
 */
export const CURRENT_REVISION: number = WIZARD_STEPS.reduce(
  (highest, step) => Math.max(highest, step.sinceRevision),
  0,
);

/**
 * The one rule the whole mechanism comes down to.
 *
 * Without a recorded revision this is a fresh install and only the setup steps apply.
 * With one, a step is due exactly if it was introduced after the user was last here —
 * which is what makes this work for every future release and not just the next one.
 */
export const dueStepIds = (lastRevision: number | null): readonly string[] =>
  WIZARD_STEPS.filter(step =>
    lastRevision === null ? step.onFreshInstall : lastRevision < step.sinceRevision,
  ).map(step => step.id);

/**
 * Resolves queued ids against the catalogue: puts them in catalogue order, drops
 * duplicates, and forgets ids the catalogue no longer knows — a step deleted in a later
 * release must not sit in someone's queue forever and block the ones behind it.
 */
export const stepsFor = (ids: readonly string[]): readonly WizardStep[] =>
  WIZARD_STEPS.filter(step => ids.includes(step.id));
